import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../../contexts/backend/AuthContext';
import { X, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/languages/LanguageContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const {
    loginStep,
    twoFactorMethod,
    rememberMePreference,
    initiateLogin,
    verify2fa,
    resetLoginFlow,
    initiatePasswordReset,
    finalizePasswordReset,
    setRememberMePreference,
    passwordResetToken,
    clearPasswordResetToken
  } = useAuth();

  type ModalView = 'login' | 'forgot-password' | 'reset-password';
  const [view, setView] = useState<ModalView>('login');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [rememberMe, setRememberMe] = useState(() => rememberMePreference);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [resetMessage, setResetMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);

  const RESET_COOLDOWN_SECONDS = 60;
  const RESET_COOLDOWN_KEY = 'npt.passwordResetCooldownUntil';

  useEffect(() => { setRememberMe(rememberMePreference); }, [rememberMePreference]);

  useEffect(() => {
    const updateCooldown = () => {
      try {
        const until = parseInt(localStorage.getItem(RESET_COOLDOWN_KEY) || '0', 10);
        setResetCooldown(Math.max(0, Math.ceil((until - Date.now()) / 1000)));
      } catch { setResetCooldown(0); }
    };
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (passwordResetToken) {
      setView('reset-password');
      setError('');
      setResetMessage('');
      setNewPassword('');
      setConfirmPassword('');
      setResetSuccess(false);
    }
  }, [passwordResetToken]);

  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (wasOpen && !isOpen) {
      setView('login');
      setError('');
      setResetMessage('');
      setIdentifier('');
      setPassword('');
      setTwoFaCode('');
      setNewPassword('');
      setConfirmPassword('');
      setResetSuccess(false);
      clearPasswordResetToken();
    }
  }, [isOpen, clearPasswordResetToken]);

  useEffect(() => {
    setError('');
    if (loginStep === 'credentials') setTwoFaCode('');
  }, [loginStep]);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      setRememberMePreference(rememberMe);
      const result = await initiateLogin(identifier, password, rememberMe);
      if (!result.success) {
        if (result.blocked) setError(t('loginModal.accountBlocked'));
        else if (result.error === 'Unauthorized.') setError(t('loginModal.loginFailed'));
        else setError(result.error || t('loginModal.loginFailed'));
      }
    } finally { setIsSubmitting(false); }
  };

  const handle2faSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const result = await verify2fa(twoFaCode, rememberMe);
      if (result.success) onClose();
      else setError(result.error || 'An unknown error occurred.');
    } finally { setIsSubmitting(false); }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetCooldown > 0) return;
    setError('');
    setIsSubmitting(true);
    const result = await initiatePasswordReset(identifier);
    if (result.success) {
      setResetMessage(t('loginModal.resetLinkSent'));
      const until = Date.now() + RESET_COOLDOWN_SECONDS * 1000;
      try { localStorage.setItem(RESET_COOLDOWN_KEY, String(until)); } catch {}
      setResetCooldown(RESET_COOLDOWN_SECONDS);
    } else {
      setError(result.error || t('loginModal.resetError'));
    }
    setIsSubmitting(false);
  };

  const resetIsValidLength = newPassword.length >= 12;
  const resetHasUpper = /[A-Z]/.test(newPassword);
  const resetHasLower = /[a-z]/.test(newPassword);
  const resetHasNumber = /[0-9]/.test(newPassword);
  const resetHasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const resetPasswordsMatch = newPassword === confirmPassword;
  const resetPasswordValid = resetIsValidLength && resetHasUpper && resetHasLower && resetHasNumber && resetHasSpecial && resetPasswordsMatch;

  const handleFinalizeReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!resetPasswordsMatch) { setError(t('loginModal.passwordsMismatch')); return; }
    if (!resetPasswordValid) { setError(t('loginModal.passwordTooShort')); return; }
    if (!passwordResetToken) return;
    setIsSubmitting(true);
    const result = await finalizePasswordReset(passwordResetToken, newPassword);
    if (result.success) setResetSuccess(true);
    else setError(result.error || t('loginModal.resetError'));
    setIsSubmitting(false);
  };

  const handleBackToLogin = () => {
    setView('login');
    setError('');
    setResetMessage('');
    setNewPassword('');
    setConfirmPassword('');
    setResetSuccess(false);
    clearPasswordResetToken();
  };

  const handleClose = () => { resetLoginFlow(); clearPasswordResetToken(); onClose(); };

  const handleGoBack = () => {
    if (view === 'forgot-password' || view === 'reset-password') handleBackToLogin();
    else resetLoginFlow();
  };

  const handleCreateAccount = () => {
    onClose();
    setTimeout(() => {
      const el = document.getElementById('pricing-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const getTitle = () => {
    if (loginStep === '2fa') return t('loginModal.enterSecurityCode');
    if (view === 'reset-password') return t('loginModal.setNewPassword');
    if (view === 'forgot-password') return t('loginModal.resetPassword');
    return t('loginModal.welcomeBack');
  };

  const getSubtitle = () => {
    if (loginStep === '2fa') return twoFactorMethod === 'app' ? t('loginModal.enterCodeFromApp') : t('loginModal.enterCodeFromEmail');
    if (view === 'reset-password') return t('loginModal.enterNewPasswordSubtitle');
    if (view === 'forgot-password') return t('loginModal.enterEmailToReset');
    return t('loginModal.signInToAccount');
  };

  if (!isOpen) return null;

  const showBack = loginStep === '2fa' || view === 'forgot-password' || (view === 'reset-password' && !resetSuccess);

  const inputCls = "w-full bg-slate-800/40 border border-slate-700/30 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/40 focus:bg-slate-800/60 transition-all duration-200 hover:border-slate-600/50";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={handleClose} />

      <div
        ref={modalRef}
        className="relative w-full max-w-[400px] bg-slate-900/95 backdrop-blur-xl border border-slate-700/30 rounded-xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          {showBack ? (
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>{t('loginModal.backToLoginButton') || 'Back'}</span>
            </button>
          ) : <div />}
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-700/40 transition-all duration-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pt-4 pb-5">
          {/* Logo + Title */}
          <div className="mb-5 text-center">
            <div className="w-10 h-10 rounded-xl bg-slate-800/40 border border-slate-700/30 flex items-center justify-center overflow-hidden p-1.5 mx-auto mb-3">
              <img
                src="./images/NPT Logo.png"
                alt="NPT"
                className="h-full w-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <h2 className="text-lg font-semibold text-white">{getTitle()}</h2>
            <p className="text-sm text-slate-400 mt-0.5">{getSubtitle()}</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-rose-300">{error}</p>
            </div>
          )}
          {resetMessage && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-emerald-300">{resetMessage}</p>
            </div>
          )}

          {/* LOGIN */}
          {loginStep === 'credentials' && view === 'login' && (
            <form onSubmit={handleCredentialsSubmit} className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t('loginModal.emailOrUsername')}
                </label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className={inputCls}
                  placeholder={t('loginModal.enterEmailOrUsername')}
                  required
                  autoFocus
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">
                    {t('loginModal.password')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setView('forgot-password')}
                    className="text-[11px] text-amber-400/80 hover:text-amber-300 transition-colors"
                  >
                    {t('loginModal.forgotPassword')}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputCls} pr-10`}
                    placeholder={t('loginModal.enterYourPassword')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none pt-0.5 group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800/40 text-amber-500 focus:ring-amber-500/30 focus:ring-offset-0"
                />
                <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                  {t('loginModal.rememberMe')}
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('loginModal.signingIn')}</span></>
                ) : (
                  <span>{t('loginModal.signIn')}</span>
                )}
              </button>

              <p className="text-center text-xs text-slate-500 pt-2">
                {t('loginModal.dontHaveAccount')}{' '}
                <button
                  type="button"
                  onClick={handleCreateAccount}
                  className="text-amber-400/80 hover:text-amber-300 transition-colors"
                >
                  {t('loginModal.createOneNow')}
                </button>
              </p>
            </form>
          )}

          {/* FORGOT PASSWORD */}
          {loginStep === 'credentials' && view === 'forgot-password' && (
            <form onSubmit={handleRequestReset} className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t('loginModal.emailAddress')}
                </label>
                <input
                  type="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className={inputCls}
                  placeholder={t('loginModal.enterEmailAddress')}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || resetCooldown > 0}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('loginModal.sendingResetLink')}</span></>
                ) : resetCooldown > 0 ? (
                  <span>{(t('loginModal.resendInSeconds') || 'Resend available in {s}s').replace('{s}', String(resetCooldown))}</span>
                ) : (
                  <span>{t('loginModal.sendResetLink')}</span>
                )}
              </button>
            </form>
          )}

          {/* RESET PASSWORD */}
          {view === 'reset-password' && !resetSuccess && (
            <form onSubmit={handleFinalizeReset} className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t('loginModal.newPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`${inputCls} pr-10`}
                    placeholder={t('loginModal.enterNewPassword')}
                    required
                    autoFocus
                    minLength={12}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t('loginModal.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`${inputCls} pr-10`}
                    placeholder={t('loginModal.repeatNewPassword')}
                    required
                    minLength={12}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {!resetPasswordsMatch && confirmPassword.length > 0 && (
                <p className="text-[11px] text-rose-400">{t('loginModal.passwordsMismatch')}</p>
              )}

              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
                <ResetReq met={resetIsValidLength} label={t('securityTab.atLeast8Characters')} />
                <ResetReq met={resetHasUpper} label={t('securityTab.uppercaseLetter')} />
                <ResetReq met={resetHasLower} label={t('securityTab.lowercaseLetter')} />
                <ResetReq met={resetHasNumber} label={t('securityTab.number')} />
                <ResetReq met={resetHasSpecial} label={t('securityTab.specialCharacter')} />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !resetPasswordValid}
                className="w-full mt-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('loginModal.resettingPassword')}</span></>
                ) : (
                  <span>{t('loginModal.saveNewPassword')}</span>
                )}
              </button>
            </form>
          )}

          {/* RESET SUCCESS */}
          {view === 'reset-password' && resetSuccess && (
            <div className="flex flex-col items-center text-center py-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">{t('loginModal.passwordResetSuccess')}</h3>
              <p className="text-xs text-slate-400 mb-5 max-w-[260px]">{t('loginModal.passwordResetSuccessMessage')}</p>
              <button
                onClick={handleBackToLogin}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25 transition-all duration-200"
              >
                {t('loginModal.backToLoginButton')}
              </button>
            </div>
          )}

          {/* 2FA */}
          {loginStep === '2fa' && (
            <form onSubmit={handle2faSubmit} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {t('loginModal.securityCode')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputCls} text-center text-lg font-medium tracking-[0.35em]`}
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('loginModal.verifying')}</span></>
                ) : (
                  <span>{t('loginModal.verifyAndSignIn')}</span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ResetReq: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
  <div className={`flex items-center gap-1.5 text-[11px] transition-colors ${met ? 'text-emerald-400' : 'text-slate-500'}`}>
    <div className={`w-1.5 h-1.5 rounded-full ${met ? 'bg-emerald-400' : 'bg-slate-700'}`} />
    {label}
  </div>
);

export default LoginModal;
